/**
 * 自动注册服务
 * 提供与后端 /email-pool/register/auto 端点的交互
 */

import { getApiBaseUrl } from '../utils/apiConfig.js';

const API_BASE_URL = getApiBaseUrl();

/**
 * 调用自动注册 API
 * @param {string} firstName - 名字
 * @param {string} lastName - 姓氏
 * @returns {Promise<Object>} 包含生成的凭证的响应
 */
export const submitAutoRegistration = async (firstName, lastName) => {
  try {
    const response = await fetch(`${API_BASE_URL}/email-pool/register/auto`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();

      // 🆕 特殊处理402积分不足错误
      if (response.status === 402) {
        throw new Error(errorData.detail || '积分不足，请充值');
      }

      throw new Error(errorData.detail || `API 请求失败: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('自动注册请求失败:', error);
    throw error;
  }
};

/**
 * 查询注册任务状态
 * @param {string} taskId - 任务 ID
 * @param {string} authToken - 认证令牌（可选）
 * @returns {Promise<Object>} 任务状态信息
 */
export const getRegistrationStatus = async (taskId, authToken = null) => {
  try {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const response = await fetch(`${API_BASE_URL}/email-pool/register/task/${taskId}`, {
      method: 'GET',
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `查询失败: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('查询注册状态失败:', error);
    throw error;
  }
};

/**
 * 复制文本到剪贴板
 * @param {string} text - 要复制的文本
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('复制到剪贴板失败:', error);
    return false;
  }
};

/**
 * 下载凭证为文本文件
 * @param {Object} credentials - 凭证对象
 * @param {string} filename - 文件名（可选）
 */
export const downloadCredentials = (credentials, filename = 'visa-credentials.txt') => {
  const content = `
=== usvisascheduling.com 注册凭证 ===

用户名: ${credentials.username}
密码: ${credentials.password}
邮箱: ${credentials.email}

安全问题和答案:
${credentials.security_questions
  .map(
    (q, index) =>
      `${index + 1}. 问题: ${q.question}
   答案: ${q.answer}`
  )
  .join('\n')}

任务 ID: ${credentials.task_id}
创建时间: ${credentials.created_at}
状态: ${credentials.status}

提示:
- 请妥善保管上述凭证，建议保存到安全的密码管理器
- 在登录 usvisascheduling.com 时需要输入用户名和密码
- 如果忘记密码，可以使用安全问题进行找回
  `;

  const element = document.createElement('a');
  element.setAttribute(
    'href',
    'data:text/plain;charset=utf-8,' + encodeURIComponent(content)
  );
  element.setAttribute('download', filename);
  element.style.display = 'none';
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
};

/**
 * 格式化注册响应为可显示的对象
 * @param {Object} response - API 响应
 * @returns {Object} 格式化后的对象
 */
export const formatRegistrationResponse = (response) => {
  return {
    taskId: response.task_id,
    username: response.username,
    password: response.password,
    email: response.email,
    securityQuestions: response.security_questions || [],
    status: response.status,
    message: response.message,
    createdAt: response.created_at,
  };
};
